import { sortBy } from 'lodash';
import {
  AnalysisSummary,
  AuditInfo,
  Identifier,
  Product,
  ProductPackage,
  ProductPrices,
} from '../types';

export interface ProductFilter {
  flags: Array<Flag>;
  searchTerm: string;
}

export interface Flag {
  name: string;
  itemsCount: number;
  filterId: string;
  filterGroupId: string;
  isSelected: boolean;
}
// This interface is specific to the offer pricing page. Does not belong here.
export interface ProductCounts {
  currentPriceCells: number;
  decreasedPriceCells: number;
  doesNotMeetFormularyPricing: number;
  increasedPriceCells: number;
  incumbentProducts: number;
  meetsFormularyPricing: number;
  mispricedCells: number;
  mispricedProducts: number;
  nonincumbentProducts: number;
  products: number;
  productsByUnspsc: {
    [key: string]: {
      count: number;
      subCategory?: Identifier;
    };
  };
  searchTermMatches: number;
  unpricedCells: number;
  unpricedProducts: number;
  validProducts: number;
  visibleProducts: number;
  visibleUnspscs: number;
}

export interface PriceIncreases {
  offerId: number;
  productId: number;
  buyer: Identifier;
  product: {
    id: number;
    catalogNumber: string;
    description: string;
  };
  currentPrice: number;
  offerPrice: number;
}

export interface OfferTiers {
  isValidForSubmission: boolean;
  tiers: Tier[];
}

export enum ProductPricingStatus {
  COMPLETE = 'COMPLETE',
  NO_PRICING = 'NO_PRICING',
  MISSING_TIER_PRICES = 'MISSING_TIER_PRICES',
  HAS_ERRORS = 'HAS_ERRORS',
  HAS_ERRORS_AND_MISSING_TIER_PRICES = 'HAS_ERRORS_AND_MISSING_TIER_PRICES',
  HAS_OFFER_PRICE_INCREASE_BASED_ON_OFFER_MARKET_SHARE = 'HAS_OFFER_PRICE_INCREASE_BASED_ON_OFFER_MARKET_SHARE',
}

export interface OfferProductStats {
  isValidForSubmission: boolean;
  productPricingStatus: ProductPricingStatus;
}

export interface Offer {
  commitmentType?: string;
  amendmentDate: string;
  marketShareTierId?: number;
  hasFormularyPrice?: boolean;
  catalogChanges: {
    changesLastAutoAppliedOn: boolean;
    autoAppliedChanges: boolean;
    records: Record[];
  };
  supportedOfferTerm?: {
    commitmentType: string;
    possiblePricingType: string[];
    possibleDurationsInMonths: number[];
  };

  prices?: IProductPricingWithFlags[];
  contractActivationMinLeadTime: any;
  cpa: OfferCpa;
  cpaId: number;
  createdDate: string;
  createdBy: string;
  currentMarketShare: number;
  currentTier?: Identifier;
  description: string;
  durations: string;
  id: number;
  isStructureComplete?: any;
  lastModifiedBy: string;
  lastModifiedDate: string;
  marketShareTier: number;
  model: any;
  name: string;
  numberOfPricedProducts: number;
  numberOfUnPricedProducts: number;
  pricingLoaded?: boolean;
  pricingTypes: string;
  productPricingStatus: any;
  rebateProxyAuthorization: boolean;
  rfp: any;
  rfpId: number;
  rfpRecipientId: number;
  seller: Identifier;
  sellerSpend?: {
    marketShare: number;
    unitMarketShare: number;
  };
  sellerId: number;
  spendLevelType: {
    name: string;
    displayName: string;
    fieldNames: string[];
    operator?: any;
  };
  status: any;
  supportedTerms: Term;
  term: Term;
  offerTiers?: OfferTiers;
  productStats?: OfferProductStats;
  isValidForSubmission?: boolean;
  totalNumberOfProducts: number;
  whenSent: string;
  selectedTier: unknown;
  auditInfo?: AuditInfo;
  unorderedTiers?: boolean;
  unorderedPricing?: boolean;
  products?: OfferProduct[];
  offerPriced?: boolean;
  priceIncreases?: PriceIncreases[];
}

export interface OfferProduct {
  productId: number;
  isIncumbentProduct: boolean;
  prices?: ProductPrices[];
  product: Product;

  formularyPrice?: number;
  pricingPackage?: ProductPackage;
}

export interface RfpOffer extends Offer {}
export class RfpOffer {
  constructor(offer: RfpOffer) {
    Object.assign(this, {
      ...offer,
      spendLevelType: offer.spendLevelType,
      commitmentType: offer.commitmentType,
      products: sortBy(
        offer.products,
        (offerProduct) => offerProduct.product?.catalogNumber,
      ),
      offerTiers: {
        isValidForSubmission: offer.offerTiers?.isValidForSubmission ?? false,
        tiers: sortBy(offer.offerTiers?.tiers ?? [], (tier) => tier.order),
      },
      marketShareTier: offer.currentTier?.id,
    });
  }
}

export interface OfferPricingDomainSpecificAnalysisSummary {
  unorderedPricingCount: number;
}

export interface OfferPricingAnalysisSummary
  extends AnalysisSummary<OfferPricingDomainSpecificAnalysisSummary> {}

interface OfferCpa {
  commitmentType: string;
  terminationDate: string;
  measured: boolean;
  contracted: boolean;
  term: Term;
  id: number;
  publishedId: string;
  effectiveDate: string;
  expirationDate: string;
  status: any;
}

export interface Term {
  isValidForSubmission?: boolean;
  commitmentTypes?: Array<string>;
  pricingTypes?: {
    name: string;
    supports: boolean;
    value: any;
  }[];
  durations?: {
    name: string;
    supports: boolean;
    value: any;
    disabled?: any;
  }[];
  duration?: number;
  targetMarketShare?: number;
  pricingType?: any;
}

export interface Record {
  actionPerformed: string;
  product: {
    name: string;
    id: number;
  };
  actionPerformedOn: string;
  changeType: string;
  offerId: number;
  actionPerformedBy: string;
}

export interface Tier {
  spendLevelType: SpendLevelType;
  unitMarketShare: number | null;
  name: string;
  absoluteDollars: number | null;
  description: string | null;
  absoluteUnits: number | null;
  id: number;
  dollarMarketShare: number;
  order: number;
  tierOrderDisplay?: number;
  tierType: 'PRIVATE' | 'STANDARD';
}

export type SpendLevelTypeName =
  | 'DOLLAR_MARKET_SHARE'
  | 'UNIT_MARKET_SHARE'
  | 'ABSOLUTE_DOLLARS'
  | 'ABSOLUTE_UNITS'
  | 'DOLLAR_MS_AND_UNITS'
  | 'UNIT_MS_AND_DOLLARS'
  | 'DOLLAR_MS_AND_DOLLARS'
  | 'UNIT_MS_AND_UNITS'
  | 'DOLLAR_MS_OR_UNITS'
  | 'UNIT_MS_OR_DOLLARS'
  | 'DOLLAR_MS_OR_DOLLARS'
  | 'UNIT_MS_OR_UNITS';

export type TierFieldNames =
  | 'dollarMarketShare'
  | 'unitMarketShare'
  | 'absoluteDollars'
  | 'absoluteUnits';

export type AndOrOr = 'AND' | 'OR';

export interface SpendLevelType {
  name: SpendLevelTypeName;
  displayName: string;
  fieldNames: TierFieldNames[];
  operator?: string | null;
}

// this is basically the object used in the grid rows
export interface IProductPricingWithFlags {
  incumbentProduct?: boolean;
  numberOfCrossReferences?: number;
  id?: number;
  prices: { [key: string]: number | null };
  pricePaid?: number | null;
  productFlags?: string[];
  uom?: string;
  unitsInPackage?: number;
  flags: { [key: string]: string[] };
  adjustedPrices: { [key: string]: number | null };
  formularyPrice?: number;
  isVisible?: boolean;
  crossed?: boolean;
}

export interface Price {
  tierId: number;
  packagePrice: number;
  providedPackagePrice: number;
}

export const ALL_SPEND_LEVEL_TYPE_LIST: SpendLevelType[] = [
  {
    name: 'DOLLAR_MARKET_SHARE',
    displayName: 'Spend Market Share',
    fieldNames: ['dollarMarketShare'],
    operator: null,
  },
  {
    name: 'UNIT_MARKET_SHARE',
    displayName: 'Units Market Share',
    fieldNames: ['unitMarketShare'],
    operator: null,
  },
  {
    name: 'ABSOLUTE_DOLLARS',
    displayName: 'Spend Volume',
    fieldNames: ['absoluteDollars'],
    operator: null,
  },
  {
    name: 'ABSOLUTE_UNITS',
    displayName: 'Units Volume',
    fieldNames: ['absoluteUnits'],
    operator: null,
  },
  {
    name: 'DOLLAR_MS_AND_UNITS',
    displayName: 'Spend MS and Units Volume',
    fieldNames: ['dollarMarketShare', 'absoluteUnits'],
    operator: 'AND',
  },
  {
    name: 'UNIT_MS_AND_DOLLARS',
    displayName: 'Units MS and Spend Volume',
    fieldNames: ['unitMarketShare', 'absoluteDollars'],
    operator: 'AND',
  },
  {
    name: 'DOLLAR_MS_AND_DOLLARS',
    displayName: 'Spend MS and Spend Volume',
    fieldNames: ['dollarMarketShare', 'absoluteDollars'],
    operator: 'AND',
  },
  {
    name: 'UNIT_MS_AND_UNITS',
    displayName: 'Units MS and Units Volume',
    fieldNames: ['unitMarketShare', 'absoluteUnits'],
    operator: 'AND',
  },
  {
    name: 'DOLLAR_MS_OR_UNITS',
    displayName: 'Spend MS or Units Volume',
    fieldNames: ['dollarMarketShare', 'absoluteUnits'],
    operator: 'OR',
  },
  {
    name: 'UNIT_MS_OR_DOLLARS',
    displayName: 'Units MS or Spend Volume',
    fieldNames: ['unitMarketShare', 'absoluteDollars'],
    operator: 'OR',
  },
  {
    name: 'DOLLAR_MS_OR_DOLLARS',
    displayName: 'Spend MS or Spend Volume',
    fieldNames: ['dollarMarketShare', 'absoluteDollars'],
    operator: 'OR',
  },
  {
    name: 'UNIT_MS_OR_UNITS',
    displayName: 'Units MS or Units Volume',
    fieldNames: ['unitMarketShare', 'absoluteUnits'],
    operator: 'OR',
  },
];
